import { FilesetResolver, PoseLandmarker } from '@mediapipe/tasks-vision';
import { 
  initializeExerciseFSMs, 
  hasFSMImplementation, 
  processFSM, 
  resetFSMRepetitionCount, 
  getFSMRepetitionCount, 
  getFSMInstance,
  analyzeBicepCurlFSM,
  analyzeRowFSM,
  analyzeCoreTwistFSM,
  analyzeLateralCoreFlexionFSM,
  analyzeCrossBodyPunchFSM,
  setBicepCurlFSMActiveSide,
  setCrossBodyPunchFSMActiveSide,
  setSeatedKneeExtensionsFSMActiveLeg,
  setSeatedMarchesFSMActiveLeg
} from './FSM/ExerciseStateMachineAdapter';


export const ExerciseType = {
  OVERHEAD_REACH: 'overhead_reach',
  ARM_ABDUCTION: 'arm_abduction',
  CROSS_BODY_PUNCH: 'cross_body_punch',
  CORE_TWIST: 'core_twist',
  LATERAL_CORE_FLEXION: 'lateral_core_flexion',
  
  
  ROW: 'row',
  BICEP_CURL: 'bicep_curl',
  SEATED_KNEE_EXTENSIONS: 'seated_knee_extensions',
  SEATED_MARCHES: 'seated_marches',
  SHOULDER_ELEVATION: 'shoulder_elevation',
  ARM_ABDUCTION_FRONT: 'arm_abduction_front'
};


let poseLandmarker = null;
// KNOX DATA CLEAR DETECTION: localStorage marker
const EXERCISE_INIT_MARKER_KEY = 'mediapipe_exercise_init_marker';

let currentExerciseType = ExerciseType.OVERHEAD_REACH;
let lastDetectionTime = 0;
let repetitionCounts = {
  [ExerciseType.OVERHEAD_REACH]: 0,
  [ExerciseType.ARM_ABDUCTION]: 0,
  [ExerciseType.CROSS_BODY_PUNCH]: 0,
  [ExerciseType.CORE_TWIST]: 0,
  [ExerciseType.LATERAL_CORE_FLEXION]: 0
};


const exerciseStates = {
  [ExerciseType.OVERHEAD_REACH]: {
    armExtensionScore: 100,
    symmetryScore: 100,
    balanceScore: 100,
    controlScore: 100,
    velocityScore: 0,
    lastArmsDownTime: 0,
    lastArmsUpTime: 0,
    reachCount: 0,
    formScore: 100,
    repFormScore: 0
  },
  [ExerciseType.ARM_ABDUCTION]: {
    armExtensionScore: 100,
    symmetryScore: 100,
    balanceScore: 100,
    controlScore: 100,
    velocityScore: 0,
    lastArmsDownTime: 0,
    lastArmsUpTime: 0,
    abductionCount: 0,
    maxAbductionAngle: 0,
    formScore: 100,
    repFormScore: 0
  },
  [ExerciseType.CROSS_BODY_PUNCH]: {
    activeSide: 'right', 
    punchCount: 0,
    punchDepth: 0,
    punchVelocity: 0,
    previousWristPosition: { x: 0, y: 0, z: 0 },
    previousTimestamp: 0,
    extensionScore: 100,
    velocityScore: 0,
    accuracyScore: 100,
    formScore: 100,
    repFormScore: 0,
    punchTimeoutStart: 0,
    lastRestTime: 0,
    lastPunchTime: 0,
    validationState: 'rest'
  },
  [ExerciseType.CORE_TWIST]: { 
    twistCount: 0,
    currentRotation: 0,
    lastDirection: null,
    maxRotationAngle: 0,
    previousRotations: [],
    rotationSmoothingFactor: 0.7,
    lastCenterTime: 0,
    lastTwistTime: 0,
    rangeOfMotionScore: 100,
    controlScore: 100,
    formScore: 100,
    repFormScore: 0,
    isTwisting: false,
    isInCenterPosition: true,
    twistTimeoutStart: 0,
    validationState: 'center'
  },
  [ExerciseType.LATERAL_CORE_FLEXION]: {
    smoothedFlexion: 0,
    previousFlexion: 0,
    previousTimestamp: 0,
    lastPositions: [],
    maxFlexionAngle: 0,
    lastUprightTime: 0,
    lastFlexionTime: 0,
    previousSpineAngles: [],
    validationState: 'neutral',
    currentSide: 'right',
    expectedSide: 'right',
    flexionCount: 0,
    rangeOfMotionScore: 100,
    lastNeutralTime: 0,
    flexionTimeoutStart: 0,
    controlScore: 100,
    formScore: 100,
    repFormScore: 0
  },
  
  
  [ExerciseType.ROW]: {
    isInExtendedPosition: true,    
    hasCompletedPullPosition: false, 
    symmetryScore: 100,            
    elbowBendScore: 100,           
    controlScore: 100,             
    velocityScore: 0,              
    postureStraightnessScore: 100, 
    lastForwardTime: 0,            
    lastPullTime: 0,               
    rowCount: 0,                   
    formScore: 100,                
    repFormScore: 0,               
    previousShoulderPosition: { x: 0, y: 0, z: 0 },
    previousTimestamp: 0,
    rowAngle: 0,                   
    maxRowAngle: 0,                
    isMovingBack: false,           
    lastPositions: []              
  },
  
  [ExerciseType.BICEP_CURL]: {
    activeSide: 'right',           
    isInExtendedPosition: true,    
    hasCompletedCurlPosition: false, 
    curlCount: 0,                  
    extensionScore: 100,           
    contractionScore: 100,         
    controlScore: 100,             
    postureStraightnessScore: 100, 
    elbowStabilityScore: 100,      
    formScore: 100,                
    repFormScore: 0,               
    lastExtendedTime: 0,           
    lastCurledTime: 0,             
    previousWristPosition: { x: 0, y: 0, z: 0 },
    previousTimestamp: 0,
    curlAngle: 0,                  
    maxCurlAngle: 0,               
    isMovingUp: false              
  },
  
  [ExerciseType.SEATED_KNEE_EXTENSIONS]: {
    activeLeg: 'right',            
    isInFlexedPosition: true,      
    hasCompletedExtension: false,  
    extensionCount: 0,             
    extensionScore: 100,           
    controlScore: 100,             
    balanceScore: 100,             
    formScore: 100,                
    repFormScore: 0,               
    lastFlexedTime: 0,             
    lastExtendedTime: 0,           
    previousAnklePosition: { x: 0, y: 0, z: 0 },
    previousTimestamp: 0,
    extensionAngle: 0,             
    maxExtensionAngle: 0,          
    isMovingForward: false,        
    hipStabilityScore: 100         
  },
  
  [ExerciseType.SEATED_MARCHES]: {
    activeLeg: 'right',            
    isInDownPosition: true,        
    hasCompletedRaisePosition: false, 
    marchCount: 0,                 
    heightScore: 100,              
    controlScore: 100,             
    balanceScore: 100,             
    postureStraightnessScore: 100, 
    formScore: 100,                
    repFormScore: 0,               
    lastDownTime: 0,               
    lastRaisedTime: 0,             
    previousKneePosition: { x: 0, y: 0, z: 0 },
    previousTimestamp: 0,
    raiseHeight: 0,                
    maxRaiseHeight: 0,             
    isMovingUp: false              
  },
  
  [ExerciseType.SHOULDER_ELEVATION]: {
    isInDownPosition: true,        
    hasCompletedElevatedPosition: false, 
    elevationCount: 0,             
    heightScore: 100,              
    symmetryScore: 100,            
    controlScore: 100,             
    formScore: 100,                
    repFormScore: 0,               
    lastDownTime: 0,               
    lastElevatedTime: 0,           
    previousShoulderPosition: { x: 0, y: 0, z: 0 },
    previousTimestamp: 0,
    elevationHeight: 0,            
    maxElevationHeight: 0,         
    isMovingUp: false,             
    lastPositions: []              
  },
  
  [ExerciseType.ARM_ABDUCTION_FRONT]: {
    isInDownPosition: true,        
    hasCompletedUpPosition: false, 
    abductionCount: 0,             
    heightScore: 100,              
    symmetryScore: 100,            
    extensionScore: 100,           
    controlScore: 100,             
    formScore: 100,                
    repFormScore: 0,               
    lastDownTime: 0,               
    lastUpTime: 0,                 
    previousWristPosition: { x: 0, y: 0, z: 0 },
    previousTimestamp: 0,
    abductionAngle: 0,             
    maxAbductionAngle: 0,          
    isMovingUp: false,             
    lastPositions: []              
  }
};


let onRepetitionCallbacks = {};
let onFormFeedbackCallbacks = {};
let onFormScoreCallbacks = {}; 


const COOLDOWN = 700; 
const MOVEMENT_THRESHOLD = 0.02;
const SMOOTHING_WINDOW_SIZE = 5; 
const MIN_TWIST_ANGLE = 25; 
const MAX_TWIST_ANGLE = 70; 
const MIN_PUNCH_EXTENSION = 0.15; 
const MIN_PUNCH_VELOCITY = 0.05; 
const MIN_REP_DURATION = 400; 
const FORM_FEEDBACK_INTERVAL = 20000; // 20 seconds between form feedbacks
const REP_COUNTDOWN_TIME = 3000;

// Add this near the top of the file with other constants
const DEBUG_LOGGING = false; // Set to true to enable debug logs


// OPTIMIZATION START - Model complexity support
export async function initExerciseDetection(qualityProfile = null) {
  // KNOX DATA CLEAR DETECTION: Check for external data clearing
  try {
    const marker = localStorage.getItem(EXERCISE_INIT_MARKER_KEY);
    if (!marker && poseLandmarker) {
      console.warn('[ExerciseDetection] 🚨 Knox data clear detected, forcing cleanup of corrupted poseLandmarker');
      try {
        poseLandmarker.close();
      } catch(e) {
        console.warn('[ExerciseDetection] poseLandmarker.close() failed:', e.message);
      }
      poseLandmarker = null;
    }
  } catch(e) {
    // localStorage not available (private mode), skip check
  }

  if (poseLandmarker) return;

  // Use quality profile if provided, otherwise default to heavy model
  const modelComplexity = qualityProfile?.pose?.modelComplexity || 'heavy';
  const delegate = qualityProfile?.pose?.delegate || 'GPU';
  const minPoseDetectionConfidence = qualityProfile?.pose?.minPoseDetectionConfidence || 0.4;
  const minPosePresenceConfidence = qualityProfile?.pose?.minPosePresenceConfidence || 0.4;
  const minTrackingConfidence = qualityProfile?.pose?.minTrackingConfidence || 0.4;

  console.log(`[ExerciseDetection] Initializing with ${modelComplexity} model, ${delegate} delegate`);

  const vision = await FilesetResolver.forVisionTasks(
    "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
  );

  poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetPath: `https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_${modelComplexity}/float16/1/pose_landmarker_${modelComplexity}.task`,
      delegate: delegate
    },
    numPoses: 1,
    runningMode: "VIDEO",
    minPoseDetectionConfidence: minPoseDetectionConfidence,
    minPosePresenceConfidence: minPosePresenceConfidence,
    minTrackingConfidence: minTrackingConfidence
  });

  // Initialize FSM implementations
  initializeExerciseFSMs();

  console.log("Pose landmarker initialized and FSMs loaded");

  // Set marker after successful init
  try {
    localStorage.setItem(EXERCISE_INIT_MARKER_KEY, Date.now().toString());
  } catch(e) {
    // localStorage not available (private mode), skip
  }
}
// OPTIMIZATION END


export function setExerciseType(exerciseType) {
  if (Object.values(ExerciseType).includes(exerciseType)) {
    
    if (currentExerciseType !== exerciseType) {
      console.log(`Switching from ${currentExerciseType} to ${exerciseType} - resetting states`);
      
      // For overhead reach, use FSM implementation 
      if (exerciseType === ExerciseType.OVERHEAD_REACH) {
        if (hasFSMImplementation(exerciseType)) {
          const fsm = getFSMInstance(exerciseType);
          if (fsm) {
            fsm.reset();
          }
        } else {
          // This fallback should never be needed but is kept for safety
          const state = exerciseStates[ExerciseType.OVERHEAD_REACH];
          state.isInDownPosition = true;
          state.hasReachedTopPosition = false;
          state.previousWristHeight = 0;
          state.lastBelowShoulderTime = 0;
          state.lastAboveHeadTime = 0;
        }
      }
      
      // For arm abduction, use FSM implementation
      if (exerciseType === ExerciseType.ARM_ABDUCTION) {
        if (hasFSMImplementation(exerciseType)) {
          const fsm = getFSMInstance(exerciseType);
          if (fsm) {
            fsm.reset();
          }
        } else {
          // This fallback should never be needed but is kept for safety
          const state = exerciseStates[ExerciseType.ARM_ABDUCTION];
          state.isInDownPosition = true;
          state.hasCompletedUpPosition = false;
          state.previousArmAngle = 0;
          state.lastArmsDownTime = 0;
          state.lastArmsUpTime = 0;
        }
      }
      
      // For core twist, use FSM implementation
      if (exerciseType === ExerciseType.CORE_TWIST) {
        if (hasFSMImplementation(exerciseType)) {
          const fsm = getFSMInstance(exerciseType);
          if (fsm) {
            fsm.reset();
          }
        } else {
          // This fallback should never be needed but is kept for safety
          const state = exerciseStates[ExerciseType.CORE_TWIST];
          state.isInCenterPosition = true;
          state.hasCompletedTwist = false;
          state.lastCenterTime = 0;
          state.lastTwistTime = 0;
        }
      }
      
      // For lateral core flexion, use FSM implementation
      if (exerciseType === ExerciseType.LATERAL_CORE_FLEXION) {
        if (hasFSMImplementation(exerciseType)) {
          const fsm = getFSMInstance(exerciseType);
          if (fsm) {
            fsm.reset();
          }
        } else {
          // This fallback should never be needed but is kept for safety
          const state = exerciseStates[ExerciseType.LATERAL_CORE_FLEXION];
          state.validationState = 'neutral';
          state.lastNeutralTime = Date.now();
          state.lastFlexionTime = 0;
          state.flexionTimeoutStart = 0;
        }
      }
      
      // For cross body punch, use FSM implementation
      if (exerciseType === ExerciseType.CROSS_BODY_PUNCH) {
        if (hasFSMImplementation(exerciseType)) {
          const fsm = getFSMInstance(exerciseType);
          if (fsm) {
            fsm.reset();
          }
        } else {
          // This fallback should never be needed but is kept for safety
          const state = exerciseStates[ExerciseType.CROSS_BODY_PUNCH];
          state.activeSide = 'right';
          state.punchState = 'guard';
          state.lastStateChangeTime = Date.now();
          state.lastRepTime = 0;
        }
      }
      
      // For row exercise, use FSM implementation
      if (exerciseType === ExerciseType.ROW) {
        if (hasFSMImplementation(exerciseType)) {
          const fsm = getFSMInstance(exerciseType);
          if (fsm) {
            fsm.reset();
          }
        } else {
          // This fallback should never be needed but is kept for safety
          const state = exerciseStates[ExerciseType.ROW];
          state.isInExtendedPosition = true;
          state.hasCompletedPullPosition = false;
          state.previousShoulderPosition = { x: 0, y: 0, z: 0 };
          state.previousTimestamp = 0;
          state.lastPositions = [];
        }
      }
      
      // For bicep curl, reset both legacy and FSM implementations
      if (exerciseType === ExerciseType.BICEP_CURL) {
        if (hasFSMImplementation(exerciseType)) {
          const fsm = getFSMInstance(exerciseType);
          if (fsm) {
            fsm.reset();
          }
        }
        
        const state = exerciseStates[ExerciseType.BICEP_CURL];
        state.activeSide = 'right';
        state.isInExtendedPosition = true;
        state.hasCompletedCurlPosition = false;
        state.previousWristPosition = { x: 0, y: 0, z: 0 };
        state.previousElbowPosition = null;
        state.previousTimestamp = 0;
        state.lastExtendedTime = 0;
        state.lastCurledTime = 0;
      }
      
      // For seated knee extensions, use FSM implementation
      if (exerciseType === ExerciseType.SEATED_KNEE_EXTENSIONS) {
        if (hasFSMImplementation(exerciseType)) {
          const fsm = getFSMInstance(exerciseType);
          if (fsm) {
            fsm.reset();
          }
        } else {
          // This fallback should never be needed but is kept for safety
          const state = exerciseStates[ExerciseType.SEATED_KNEE_EXTENSIONS];
          state.activeLeg = 'right';
          state.isInFlexedPosition = true;
          state.hasCompletedExtension = false;
          state.previousAnklePosition = { x: 0, y: 0, z: 0 };
          state.previousTimestamp = 0;
          state.lastFlexedTime = 0;
          state.lastExtendedTime = 0;
        }
      }
      
      // For seated marches, use FSM implementation
      if (exerciseType === ExerciseType.SEATED_MARCHES) {
        if (hasFSMImplementation(exerciseType)) {
          const fsm = getFSMInstance(exerciseType);
          if (fsm) {
            fsm.reset();
          }
        } else {
          // This fallback should never be needed but is kept for safety
          const state = exerciseStates[ExerciseType.SEATED_MARCHES];
          state.activeLeg = 'right';
          state.isInDownPosition = true;
          state.hasCompletedRaisePosition = false;
          state.previousKneePosition = { x: 0, y: 0, z: 0 };
          state.previousTimestamp = 0;
          state.lastDownTime = 0;
          state.lastRaisedTime = 0;
        }
      }
      
      lastDetectionTime = 0;
    }
    
    currentExerciseType = exerciseType;
    console.log(`Exercise type set to: ${exerciseType}`);
    return true;
  }
  return false;
}


export function getCurrentExerciseType() {
  return currentExerciseType;
}


export function setOnRepetitionCallback(exerciseType, callback) {
  onRepetitionCallbacks[exerciseType] = callback;
  if (DEBUG_LOGGING) console.log(`Repetition callback set for ${exerciseType}`);
}


export function setOnFormFeedbackCallback(exerciseType, callback) {
  onFormFeedbackCallbacks[exerciseType] = callback;
}


export function setOnFormScoreCallback(exerciseType, callback) {
  onFormScoreCallbacks[exerciseType] = callback;
}


export function getRepetitionCount(exerciseType = currentExerciseType) {
  // Check if FSM implementation is available
  if (hasFSMImplementation(exerciseType)) {
    return getFSMRepetitionCount(exerciseType);
  }
  
  // Fall back to legacy implementation
  return repetitionCounts[exerciseType] || 0;
}


export function getFormScores(exerciseType = currentExerciseType) {
  // Check if FSM implementation is available
  if (hasFSMImplementation(exerciseType)) {
    const fsm = getFSMInstance(exerciseType);
    if (fsm) {
      return fsm.formScores;
    }
  }

  // Fall back to legacy implementation
  const state = exerciseStates[exerciseType];
  
  if (!state) return { overall: 0 };
  
  let scores = { overall: state.formScore || 0 };
  
  // Add specific exercise scores
  switch (exerciseType) {
    case ExerciseType.OVERHEAD_REACH:
      scores = {
        ...scores,
        rangeOfMotion: state.rangeOfMotion || 0,
        velocity: state.velocityScore || 0,
        control: state.movementSmoothness || 0
      };
      break;
    case ExerciseType.ARM_ABDUCTION:
      scores = {
        ...scores,
        rangeOfMotion: state.rangeOfMotion || 0,
        symmetry: state.symmetryScore || 0,
        control: state.controlScore || 0
      };
      break;
    case ExerciseType.LATERAL_CORE_FLEXION:
      scores = {
        ...scores,
        rangeOfMotion: state.rangeOfMotionScore || 0,
        control: state.controlScore || 0
      };
      break;
    // ... other exercises ...
  }
  
  return scores;
}


export async function detectExercise(video, timestamp) {
  if (!poseLandmarker) return null;
  
  // Check if video has valid dimensions before processing
  if (!video || video.videoWidth === 0 || video.videoHeight === 0 || 
      video.readyState < video.HAVE_METADATA) {
    return null;
  }
  
  let results;
  try {
    results = await poseLandmarker.detectForVideo(video, timestamp);
  } catch (error) {
    console.warn("Exercise detection error:", error.message);
    return null;
  }
  const currentTime = Date.now();
  
  // Check if we're in cooldown period
  let canCountRep = currentTime - lastDetectionTime >= COOLDOWN;
  
  // Analyze the exercise
  const detectionResult = analyzeExercise(results, currentExerciseType, timestamp);
  
  // Debug output
  if (detectionResult.isRepCompleted) {
    if (DEBUG_LOGGING) {
      console.log(`Exercise ${currentExerciseType} detected a rep!`);
      console.log(`Can count rep: ${canCountRep ? 'YES' : 'NO - cooldown active'}`);
    }
  }
  
  // Handle repetition counting
  if (detectionResult.isRepCompleted && canCountRep) {
    lastDetectionTime = currentTime;
    
    // For exercises using FSM, the count is managed by the FSM
    if (!hasFSMImplementation(currentExerciseType)) {
      repetitionCounts[currentExerciseType]++;
    }
    
    // For callback/UI purposes, get the current count (either from FSM or legacy)
    const currentCount = getRepetitionCount(currentExerciseType);
    
    if (DEBUG_LOGGING) {
      console.log(`Rep completed for ${currentExerciseType}! Count: ${currentCount}`);
      console.log(`Callback registered: ${!!onRepetitionCallbacks[currentExerciseType]}`);
    }
    
    // Call the registered callback if any
    if (onRepetitionCallbacks[currentExerciseType]) {
      try {
        onRepetitionCallbacks[currentExerciseType](
          currentCount, 
          detectionResult.formFeedback,
          detectionResult.exerciseState
        );
        if (DEBUG_LOGGING) console.log("Callback executed successfully");
      } catch (error) {
        console.error("Error in repetition callback:", error);
      }
    } else {
      if (DEBUG_LOGGING) console.warn(`No callback registered for ${currentExerciseType}`);
    }
  }

  // Update form scores UI
  if (onFormScoreCallbacks[currentExerciseType]) {
    onFormScoreCallbacks[currentExerciseType](
      getFormScores(currentExerciseType)
    );
  }
  
  // Return the detection result
  return {
    exerciseType: currentExerciseType,
    repetitionCount: getRepetitionCount(currentExerciseType),
    isRepCompleted: detectionResult.isRepCompleted && canCountRep,
    landmarks: results.landmarks,
    worldLandmarks: results.worldLandmarks,
    formFeedback: detectionResult.formFeedback,
    formScores: getFormScores(currentExerciseType),
    exerciseState: detectionResult.exerciseState
  };
}


// NEW: Get per-rep history for session analytics
export function getRepHistory(exerciseType = currentExerciseType) {
  // Check if FSM implementation is available
  if (hasFSMImplementation(exerciseType)) {
    const fsm = getFSMInstance(exerciseType);
    if (fsm) {
      return fsm.repHistory || [];
    }
  }

  // Legacy exercises don't track per-rep history
  return [];
}


export function resetRepetitionCount(exerciseType = null) {
  if (exerciseType) {
    repetitionCounts[exerciseType] = 0;
    // Reset FSM repetition count if available
    if (hasFSMImplementation(exerciseType)) {
      resetFSMRepetitionCount(exerciseType);
    }
    if (DEBUG_LOGGING) console.log(`Reset repetition count for ${exerciseType}`);
  } else {
    // Reset all repetition counts
    Object.keys(repetitionCounts).forEach(type => {
      repetitionCounts[type] = 0;
      // Reset FSM repetition count if available
      if (hasFSMImplementation(type)) {
        resetFSMRepetitionCount(type);
      }
    });
    if (DEBUG_LOGGING) console.log("Reset all repetition counts");
  }
}


export function resetBicepCurl() {
  repetitionCounts[ExerciseType.BICEP_CURL] = 0;
  const state = exerciseStates[ExerciseType.BICEP_CURL];
  state.activeSide = 'right';
  state.isInExtendedPosition = true;
  state.hasCompletedCurlPosition = false;
  state.previousWristPosition = { x: 0, y: 0, z: 0 };
  state.previousElbowPosition = null;
  state.previousTimestamp = 0;
  state.lastExtendedTime = 0;
  state.lastCurledTime = 0;
  if (DEBUG_LOGGING) console.log("Reset bicep curl counter and state");
}


function analyzeExercise(results, exerciseType, timestamp) {
  // Skip if no results are available
  if (!results || !results.landmarks || !results.landmarks[0]) {
    return { isRepCompleted: false, formFeedback: null, exerciseState: exerciseStates[exerciseType] };
  }
  
  // Try to use FSM implementation if available
  if (hasFSMImplementation(exerciseType)) {
    try {
      // Process using FSM
      if (DEBUG_LOGGING) {
        if (exerciseType === ExerciseType.LATERAL_CORE_FLEXION) {
          console.log("Using FSM for Lateral Core Flexion");
        } else if (exerciseType === ExerciseType.SEATED_KNEE_EXTENSIONS) {
          console.log("Using FSM for Seated Knee Extensions");
        } else if (exerciseType === ExerciseType.SEATED_MARCHES) {
          console.log("Using FSM for Seated Marches");
        }
      }
      
      const fsmResult = processFSM(exerciseType, results, timestamp);
      
      // NOTE: Don't increment repetitionCounts here - FSM manages its own internal count
      // and detectExercise() handles legacy exercise counting via getRepetitionCount()
      
      // Update exercise state with FSM state
      exerciseStates[exerciseType] = { ...exerciseStates[exerciseType], ...fsmResult.exerciseState };
      
      return fsmResult;
    } catch (error) {
      console.error(`Error using FSM for ${exerciseType}:`, error);
      // Fall back to legacy implementation if needed
      if (exerciseType === ExerciseType.OVERHEAD_REACH || 
          exerciseType === ExerciseType.ARM_ABDUCTION ||
          exerciseType === ExerciseType.CORE_TWIST ||
          exerciseType === ExerciseType.LATERAL_CORE_FLEXION ||
          exerciseType === ExerciseType.CROSS_BODY_PUNCH ||
          exerciseType === ExerciseType.SEATED_KNEE_EXTENSIONS ||
          exerciseType === ExerciseType.SEATED_MARCHES) {
        return { isRepCompleted: false, formFeedback: "Error in exercise detection", exerciseState: {} };
      }
    }
  }
  
  // Fall back to non-FSM implementations
  switch (exerciseType) {
    case ExerciseType.CROSS_BODY_PUNCH:
      return analyzeCrossBodyPunch(results, timestamp);
    case ExerciseType.SEATED_KNEE_EXTENSIONS:
      return analyzeSeatedKneeExtensions(results, timestamp);
    case ExerciseType.SEATED_MARCHES:
      return analyzeSeatedMarches(results, timestamp);
    case ExerciseType.SHOULDER_ELEVATION:
      return analyzeShoulderElevation(results, timestamp);
    case ExerciseType.ARM_ABDUCTION_FRONT:
      return analyzeArmAbductionFront(results, timestamp);
    default:
      return { isRepCompleted: false, formFeedback: null, exerciseState: exerciseStates[exerciseType] };
  }
}


function analyzeCrossBodyPunch(results, timestamp) {
  const pose = results.landmarks[0];
  const worldPose = results.worldLandmarks[0];
  const state = exerciseStates[ExerciseType.CROSS_BODY_PUNCH];
  
  
  if (!worldPose || !worldPose[11] || !worldPose[12] || !worldPose[15] || !worldPose[16]) {
    return { isRepCompleted: false, formFeedback: null, exerciseState: state };
  }
  
  const nose = pose[0];
  const leftShoulder = worldPose[11];
  const rightShoulder = worldPose[12];
  const leftWrist = worldPose[15];
  const rightWrist = worldPose[16];
  
  
  const activeSide = state.activeSide || 'right';
  const activeWrist = activeSide === 'right' ? rightWrist : leftWrist;
  const activeShoulder = activeSide === 'right' ? rightShoulder : leftShoulder;
  const oppositeShoulder = activeSide === 'right' ? leftShoulder : rightShoulder;
  
  
  
  
  const hasFullyCrossedBody = 
    (activeSide === 'right' && activeWrist.x < oppositeShoulder.x) ||
    (activeSide === 'left' && activeWrist.x > oppositeShoulder.x);
  
  
  const isInGuardPosition = 
    (activeSide === 'right' && rightWrist.x > (rightShoulder.x - 0.15)) ||
    (activeSide === 'left' && leftWrist.x < (leftShoulder.x + 0.15));
  
  
  const timeDelta = timestamp - (state.previousTimestamp || timestamp);
  if (timeDelta > 0 && state.previousWristPosition) {
    const distX = activeWrist.x - state.previousWristPosition.x;
    const distY = activeWrist.y - state.previousWristPosition.y;
    const distZ = activeWrist.z - state.previousWristPosition.z;
    const distance = Math.sqrt(distX * distX + distY * distY + distZ * distZ);
    state.punchVelocity = distance / timeDelta * 1000; 
  }
  
  
  state.previousWristPosition = {
    x: activeWrist.x,
    y: activeWrist.y,
    z: activeWrist.z
  };
  state.previousTimestamp = timestamp;
  
  
  
  
  if (state.punchState === undefined) {
    state.punchState = 'guard';   
    state.lastStateChangeTime = timestamp;
    state.lastRepTime = 0;
  }
  
  
  const MIN_TIME_BETWEEN_REPS = 800; 
  const MIN_TIME_IN_POSITION = 100; 
  
  let repCompleted = false;
  
  
  state.extensionScore = Math.min(100, Math.max(0, hasFullyCrossedBody ? 100 : 50));
  state.velocityScore = Math.min(100, Math.max(0, (state.punchVelocity / 2.0) * 100));
  
  
  switch (state.punchState) {
    case 'guard':
      
      if (hasFullyCrossedBody && !isInGuardPosition) {
        state.punchState = 'crossed';
        state.lastStateChangeTime = timestamp;
      }
      break;
      
    case 'crossed':
      
      if (isInGuardPosition && !hasFullyCrossedBody && timestamp - state.lastStateChangeTime > MIN_TIME_IN_POSITION) {
        
        if (timestamp - state.lastRepTime > MIN_TIME_BETWEEN_REPS) {
          
          state.punchCount++;
          repCompleted = true;
          state.lastRepTime = timestamp;
          
          
          state.activeSide = activeSide === 'right' ? 'left' : 'right';
          
          
          state.repFormScore = Math.round(
            (state.extensionScore * 0.6) + 
            (state.velocityScore * 0.4)
          );
          
          
          state.formScore = state.repFormScore;
        }
        
        
        state.punchState = 'guard';
        state.lastStateChangeTime = timestamp;
      }
      break;
  }
  
  
  if (state.punchState === 'crossed' && timestamp - state.lastStateChangeTime > 5000) {
    state.punchState = 'guard';
    state.lastStateChangeTime = timestamp;
  }

  
  let formFeedback = null;
  
  if (hasFullyCrossedBody) {
    
    const idealPunchHeight = (oppositeShoulder.y + 0.1); 
    if (Math.abs(activeWrist.y - idealPunchHeight) > 0.2) {
      formFeedback = `Adjust punch height ${activeWrist.y < idealPunchHeight ? 'down' : 'up'} for better form`;
    }
  } else if (state.punchState === 'crossed' && !hasFullyCrossedBody) {
    formFeedback = "Return to guard position to complete the punch";
  } else if (state.punchState === 'guard' && !hasFullyCrossedBody) {
    
    if (Math.random() < 0.05) {
      formFeedback = "Extend punch fully across body for proper form";
    }
  }
  
  
  if (!state.formScore) state.formScore = 80;
  
  if (state.repFormScore) {
    
    state.formScore = Math.round(state.formScore * 0.9 + state.repFormScore * 0.1);
  } else {
    
    const currentFormScore = Math.round(
      (state.extensionScore * 0.6) + 
      (state.velocityScore * 0.4)
    );
    state.formScore = Math.round(state.formScore * 0.9 + currentFormScore * 0.1);
  }
  
  return { isRepCompleted: repCompleted, formFeedback, exerciseState: state };
}

function analyzeCoreRotation(results, timestamp) {
  const pose = results.landmarks[0];
  const worldPose = results.worldLandmarks[0];
  const state = exerciseStates[ExerciseType.CORE_TWIST];
  
  
  if (!state.currentTwistAngle) state.currentTwistAngle = 0;
  if (!state.previousTwistAngle) state.previousTwistAngle = 0;
  if (!state.twistDirection) state.twistDirection = 'center';
  if (!state.lastCenterTime) state.lastCenterTime = timestamp;
  if (!state.lastTwistTime) state.lastTwistTime = 0;
  if (!state.maxRightTwistAngle) state.maxRightTwistAngle = 0;
  if (!state.maxLeftTwistAngle) state.maxLeftTwistAngle = 0;
  if (!state.twistHistory) state.twistHistory = [];
  if (!state.formScore) state.formScore = 80;
  
  
  if (!worldPose || !worldPose[11] || !worldPose[12] || !worldPose[23] || !worldPose[24]) {
    return { isRepCompleted: false, formFeedback: null, exerciseState: state };
  }
  
  const leftShoulder = worldPose[11];
  const rightShoulder = worldPose[12];
  const leftHip = worldPose[23];
  const rightHip = worldPose[24];
  
  
  
  
  const shoulderVector = {
    x: rightShoulder.x - leftShoulder.x,
    z: rightShoulder.z - leftShoulder.z
  };
  
  const hipVector = {
    x: rightHip.x - leftHip.x,
    z: rightHip.z - leftHip.z
  };
  
  
  const shoulderAngle = Math.atan2(shoulderVector.z, shoulderVector.x);
  const hipAngle = Math.atan2(hipVector.z, hipVector.x);
  let rotationAngle = shoulderAngle - hipAngle;
  
  
  rotationAngle = rotationAngle * (180 / Math.PI);
  if (rotationAngle > 180) rotationAngle -= 360;
  if (rotationAngle < -180) rotationAngle += 360;
  
  
  const smoothingFactor = 0.3;
  if (state.currentTwistAngle === undefined) {
    state.currentTwistAngle = rotationAngle;
  } else {
    state.currentTwistAngle = (state.currentTwistAngle * (1 - smoothingFactor)) + 
                             (rotationAngle * smoothingFactor);
  }
  
  
  if (state.twistHistory.length > 10) state.twistHistory.shift();
  state.twistHistory.push({angle: state.currentTwistAngle, time: timestamp});
  
  
  let twistVelocity = 0;
  if (state.twistHistory.length >= 2) {
    const first = state.twistHistory[0];
    const last = state.twistHistory[state.twistHistory.length - 1];
    const timeDiff = (last.time - first.time) / 1000;
    if (timeDiff > 0) {
      const angleDiff = Math.abs(last.angle - first.angle);
      twistVelocity = angleDiff / timeDiff;
    }
  }
  
  
  
  
  const CENTER_THRESHOLD = 7;   
  const TWIST_THRESHOLD = 10;   
  
  
  const isCentered = Math.abs(state.currentTwistAngle) < CENTER_THRESHOLD;
  const isTwistedRight = state.currentTwistAngle > TWIST_THRESHOLD;
  const isTwistedLeft = state.currentTwistAngle < -TWIST_THRESHOLD;
  
  
  const previousDirection = state.twistDirection;
  
  if (isCentered) {
    state.twistDirection = 'center';
  } else if (isTwistedRight) {
    state.twistDirection = 'right';
    
    if (state.currentTwistAngle > state.maxRightTwistAngle) {
      state.maxRightTwistAngle = state.currentTwistAngle;
    }
  } else if (isTwistedLeft) {
    state.twistDirection = 'left';
    
    if (state.currentTwistAngle < state.maxLeftTwistAngle) {
      state.maxLeftTwistAngle = state.currentTwistAngle;
    }
  }
  
  
  
  let repCompleted = false;
  
  
  
  
  
  
  
  
  
  
  
  
  if (state.twistDirection === 'center' && 
      previousDirection === 'right' && 
      state.maxRightTwistAngle > TWIST_THRESHOLD &&
      timestamp - state.lastTwistTime > 500) {
    
    repCompleted = true;
    state.lastTwistTime = timestamp;
    
    
    const rangeScore = Math.min(100, (state.maxRightTwistAngle / 60) * 100);
    const controlScore = Math.min(100, Math.max(0, 100 - (twistVelocity > 180 ? (twistVelocity - 180) / 5 : 0)));
    
    state.repFormScore = Math.round((rangeScore * 0.7) + (controlScore * 0.3));
    state.formScore = state.repFormScore;
    
    
    state.maxRightTwistAngle = 0;
  }
  
  
  else if (state.twistDirection === 'center' && 
           previousDirection === 'left' && 
           state.maxLeftTwistAngle < -TWIST_THRESHOLD &&
           timestamp - state.lastTwistTime > 500) {
    
    repCompleted = true;
    state.lastTwistTime = timestamp;
    
    
    const rangeScore = Math.min(100, (Math.abs(state.maxLeftTwistAngle) / 60) * 100);
    const controlScore = Math.min(100, Math.max(0, 100 - (twistVelocity > 180 ? (twistVelocity - 180) / 5 : 0)));
    
    state.repFormScore = Math.round((rangeScore * 0.7) + (controlScore * 0.3));
    state.formScore = state.repFormScore;
    
    
    state.maxLeftTwistAngle = 0;
  }
  
  
  
  let formFeedback = null;
  
  
  if (Math.abs(state.currentTwistAngle) > 70) {
    formFeedback = "Reduce twist range for safety";
  }
  
  
  if (twistVelocity > 300) {
    formFeedback = "Slow down for better control";
  }
  
  
  
  
  const currentRangeOfMotion = Math.min(100, Math.max(0, (Math.abs(state.currentTwistAngle) / 60) * 100));
  const currentControlScore = Math.min(100, Math.max(0, 100 - (twistVelocity > 180 ? (twistVelocity - 180) / 5 : 0)));
  
  
  if (state.repFormScore) {
    
    state.formScore = Math.round(state.formScore * 0.9 + state.repFormScore * 0.1);
  } else {
    
    const currentFormScore = Math.round(
      (currentRangeOfMotion * 0.7) + 
      (currentControlScore * 0.3)
    );
    state.formScore = Math.round(state.formScore * 0.9 + currentFormScore * 0.1);
  }
  
  return { isRepCompleted: repCompleted, formFeedback, exerciseState: state };
}

function analyzeLateralFlexion(results, timestamp) {
  const pose = results.landmarks[0];
  const state = exerciseStates[ExerciseType.LATERAL_CORE_FLEXION];

  
  if (!pose || !pose[11] || !pose[12] || !pose[23] || !pose[24]) {
    return { isRepCompleted: false, formFeedback: null, exerciseState: state };
  }

  
  const leftShoulder = pose[11]; 
  const rightShoulder = pose[12];
  const leftHip = pose[23];
  const rightHip = pose[24];

  
  
  const shoulderMidpoint = {
    x: (leftShoulder.x + rightShoulder.x) / 2,
    y: (leftShoulder.y + rightShoulder.y) / 2
  };
  
  const hipMidpoint = {
    x: (leftHip.x + rightHip.x) / 2,
    y: (leftHip.y + rightHip.y) / 2
  };
  
  
  const shoulderAngleRad = Math.atan2(rightShoulder.y - leftShoulder.y, rightShoulder.x - leftShoulder.x);
  const shoulderAngleDeg = shoulderAngleRad * (180 / Math.PI);
  
  
  const spineVectorX = shoulderMidpoint.x - hipMidpoint.x;
  const spineVectorY = shoulderMidpoint.y - hipMidpoint.y;
  const spineAngleRad = Math.atan2(spineVectorX, -spineVectorY); 
  const spineAngleDeg = spineAngleRad * (180 / Math.PI);
  
  
  const currentAngle = spineAngleDeg * 0.7 + shoulderAngleDeg * 0.3;

  
  let areBothHandsRaised = false;
  if (pose[15] && pose[16]) { 
    const leftWrist = pose[15];
    const rightWrist = pose[16];
    const isLeftHandRaised = leftWrist.y < leftShoulder.y;
    const isRightHandRaised = rightWrist.y < rightShoulder.y;
    areBothHandsRaised = isLeftHandRaised && isRightHandRaised;
  }
  
  
  if (!state.angleHistory) {
    state.angleHistory = [];
  }
  
  
  state.angleHistory.push({ 
    angle: currentAngle, 
    time: timestamp,
    smoothedValue: state.smoothedAngle || currentAngle
  });
  
  if (state.angleHistory.length > 10) { 
    state.angleHistory.shift();
  }
  
  
  const alpha = 0.3; 
  let smoothedAngle;
  
  if (!state.smoothedAngle) {
    smoothedAngle = currentAngle;
  } else {
    smoothedAngle = alpha * currentAngle + (1 - alpha) * state.smoothedAngle;
  }
  state.smoothedAngle = smoothedAngle;

  
  let angleVelocity = 0;
  if (state.angleHistory.length >= 3) {
    const newest = state.angleHistory[state.angleHistory.length - 1];
    const oldest = state.angleHistory[state.angleHistory.length - 3];
    const timeDiff = (newest.time - oldest.time) / 1000; 
    
    if (timeDiff > 0.05) { 
      const angleDiff = Math.abs(newest.angle - oldest.angle);
      angleVelocity = angleDiff / timeDiff;
    }
  }
  
  const isStable = angleVelocity < 15; 
  
  
  const NEUTRAL_THRESHOLD = 4;  
  const FLEXION_THRESHOLD = 8;  
  const MIN_HOLD_DURATION = 200; 
  
  const direction = smoothedAngle > 0 ? 'right' : 'left';
  const absAngle = Math.abs(smoothedAngle);
  const isNeutral = absAngle < NEUTRAL_THRESHOLD;
  const isFlexed = absAngle > FLEXION_THRESHOLD;
  
  
  if (!state.validationState) state.validationState = 'neutral';
  if (!state.lastNeutralTime) state.lastNeutralTime = timestamp;
  if (!state.flexionStartTime) state.flexionStartTime = 0;
  if (!state.currentDirection) state.currentDirection = 'none';
  if (!state.maxFlexionAngle) state.maxFlexionAngle = 0; 
  if (!state.completedReps) state.completedReps = { left: 0, right: 0 };
  
  
  let repCompleted = false;

  switch (state.validationState) {
    case 'neutral':
      
      if (isFlexed && isStable) {
        state.validationState = 'flexion';
        state.currentDirection = direction;
        state.flexionStartTime = timestamp;
        state.maxFlexionAngle = absAngle; 
      }
      break;
      
    case 'flexion':
      
      if (absAngle > Math.abs(state.maxFlexionAngle)) {
        state.maxFlexionAngle = smoothedAngle; 
      }
      
      const flexionDuration = timestamp - state.flexionStartTime;
      const significantFlexion = Math.abs(state.maxFlexionAngle) > FLEXION_THRESHOLD;
      
      
      if (flexionDuration >= MIN_HOLD_DURATION && isNeutral && significantFlexion) {
        const oldDirection = state.currentDirection;
        
        state.validationState = 'neutral';
        state.lastNeutralTime = timestamp;
        state.completedReps[oldDirection]++;
        repCompleted = true;
        
        
        const optimalFlexionAngle = 20; 
        const rangeScore = Math.min(100, 100 - Math.abs(Math.abs(state.maxFlexionAngle) - optimalFlexionAngle) * 3);
        const controlScore = Math.min(100, Math.max(0, 100 - angleVelocity * 2));
        
        state.rangeOfMotionScore = rangeScore;
        state.controlScore = controlScore;
        state.repFormScore = Math.round((rangeScore * 0.7) + (controlScore * 0.3));
        state.formScore = state.repFormScore;
        
        
        state.maxFlexionAngle = 0;
        state.currentDirection = 'none';
      } 
      
      else if (state.currentDirection !== 'none' && 
               state.currentDirection !== direction && 
               isFlexed && 
               flexionDuration >= MIN_HOLD_DURATION) {
        
        const oldDirection = state.currentDirection;
        
        
        state.completedReps[oldDirection]++;
        repCompleted = true;

        
        const optimalFlexionAngle = 20; 
        const rangeScore = Math.min(100, 100 - Math.abs(Math.abs(state.maxFlexionAngle) - optimalFlexionAngle) * 3);
        const controlScore = Math.min(100, Math.max(0, 100 - angleVelocity * 2));
        
        state.rangeOfMotionScore = rangeScore;
        state.controlScore = controlScore;
        state.repFormScore = Math.round((rangeScore * 0.7) + (controlScore * 0.3));
        state.formScore = state.repFormScore;
        
        
        state.currentDirection = direction;
        state.flexionStartTime = timestamp;
        state.maxFlexionAngle = smoothedAngle;
      }
      
      else if (timestamp - state.flexionStartTime > 5000) {
        state.validationState = 'neutral';
        state.maxFlexionAngle = 0;
        state.currentDirection = 'none';
      }
      break;
  }
  
  
  let formFeedback = null;
  const MAX_FLEXION_ANGLE = 30; 
  const actualFlexionAngle = Math.abs(smoothedAngle);
  
  if (Math.random() < 0.05) { 
    if (actualFlexionAngle > MAX_FLEXION_ANGLE) {
      formFeedback = "Reduce bend angle for safety";
    } else if (state.validationState === 'flexion' && 
               actualFlexionAngle < FLEXION_THRESHOLD + 2) {
      formFeedback = "Bend further for better ROM";
    } else if (angleVelocity > 30) {
      formFeedback = "Move more slowly for better control";
    }
    
    if (!areBothHandsRaised && Math.random() < 0.2) { 
      formFeedback = "Remember to keep hands raised if doing that variation";
    }
  }
  
  
  if (!state.formScore) state.formScore = 80;
  if (state.repFormScore) {
    state.formScore = 0.9 * state.formScore + 0.1 * state.repFormScore;
  }
  
  state.flexionCount = state.completedReps.left + state.completedReps.right;
  
  state.currentAngle = smoothedAngle;
  state.angleVelocity = angleVelocity;
  
  return { 
    isRepCompleted: repCompleted, 
    formFeedback, 
    exerciseState: state 
  };
}


function calculateLateralFlexionScore(state) {
  
  const romScore = state.rangeOfMotionScore || 100;
  const controlScore = state.controlScore || 100;
  
  
  return Math.round(romScore * 0.9 + controlScore * 0.1);
}


function calculateVelocity(currentPoint, previousPoint, timeDelta) {
  if (!previousPoint || timeDelta === 0) return 0;
  
  const dx = currentPoint.x - previousPoint.x;
  const dy = currentPoint.y - previousPoint.y;
  const dz = currentPoint.z - previousPoint.z;
  
  const distance = Math.sqrt(dx*dx + dy*dy + dz*dz);
  return distance / (timeDelta / 1000); 
}


function calculateJointAngle(a, b, c) {
  
  const vectorBA = {
    x: a.x - b.x,
    y: a.y - b.y,
    z: a.z - b.z
  };
  
  const vectorBC = {
    x: c.x - b.x,
    y: c.y - b.y,
    z: c.z - b.z
  };
  
  
  const dotProduct = vectorBA.x * vectorBC.x + vectorBA.y * vectorBC.y + vectorBA.z * vectorBC.z;
  
  
  const magnitudeBA = Math.sqrt(vectorBA.x * vectorBA.x + vectorBA.y * vectorBA.y + vectorBA.z * vectorBA.z);
  const magnitudeBC = Math.sqrt(vectorBC.x * vectorBC.x + vectorBC.y * vectorBC.y + vectorBC.z * vectorBC.z);
  
  
  const angle = Math.acos(dotProduct / (magnitudeBA * magnitudeBC)) * (180 / Math.PI);
  
  return angle;
}


function normalizeAngle(angle) {
  if (angle > 180) {
    return 360 - angle;
  } else if (angle < -180) {
    return 360 + angle;
  }
  return Math.abs(angle);
}


export function recordExerciseData(exerciseType, pointMeshes, feedback, metadata) {
  
  
  
  console.log("Recording exercise data:", exerciseType, metadata);
}


function analyzeRow(results, timestamp) {
  const pose = results.landmarks[0];
  const worldPose = results.worldLandmarks[0];
  const state = exerciseStates[ExerciseType.ROW];
  
  
  if (!worldPose || !worldPose[11] || !worldPose[12] || !worldPose[13] || !worldPose[14] || !worldPose[15] || !worldPose[16] || !worldPose[23] || !worldPose[24]) {
    return { isRepCompleted: false, formFeedback: null, exerciseState: state };
  }
  
  
  const leftShoulder = worldPose[11];
  const rightShoulder = worldPose[12];
  const leftElbow = worldPose[13];
  const rightElbow = worldPose[14];
  const leftWrist = worldPose[15];
  const rightWrist = worldPose[16];
  const leftHip = worldPose[23];
  const rightHip = worldPose[24];
  
  
  if (!state.lastPositions) state.lastPositions = [];
  if (!state.movingAverageElbowAngle) state.movingAverageElbowAngle = 160;
  if (!state.controlScore) state.controlScore = 100;
  if (!state.positionHistory) state.positionHistory = [];
  if (!state.kalmanFilters) {
    
    state.kalmanFilters = {
      elbowWidth: {x: 0.3, p: 1, q: 0.01, r: 0.1}, 
      wristShoulderXDiff: {x: 0.2, p: 1, q: 0.01, r: 0.1},
      elbowAngle: {x: 160, p: 1, q: 0.01, r: 0.1},
    };
  }
  
  
  
  
  
  
  
  
  
  const shoulderWidth = calculateDistance3D(leftShoulder, rightShoulder);
  
  
  const rawElbowWidth = calculateDistance3D(leftElbow, rightElbow);
  
  
  
  
  const leftWristElbowYDiff = leftWrist.y - leftElbow.y;
  const rightWristElbowYDiff = rightWrist.y - rightElbow.y;
  const avgWristElbowYDiff = (leftWristElbowYDiff + rightWristElbowYDiff) / 2;
  
  
  
  const leftWristShoulderXDiff = Math.abs(leftWrist.x - leftShoulder.x);
  const rightWristShoulderXDiff = Math.abs(rightWrist.x - rightShoulder.x);
  const rawWristShoulderXDiff = (leftWristShoulderXDiff + rightWristShoulderXDiff) / 2;
  
  
  const leftElbowAngle = calculateJointAngle(leftShoulder, leftElbow, leftWrist);
  const rightElbowAngle = calculateJointAngle(rightShoulder, rightElbow, rightWrist);
  const rawElbowAngle = (leftElbowAngle + rightElbowAngle) / 2;
  
  
  
  const elbowWidth = applyKalmanFilter(state.kalmanFilters.elbowWidth, rawElbowWidth);
  const avgWristShoulderXDiff = applyKalmanFilter(state.kalmanFilters.wristShoulderXDiff, rawWristShoulderXDiff);
  const avgElbowAngle = applyKalmanFilter(state.kalmanFilters.elbowAngle, rawElbowAngle);
  
  
  const smoothingFactor = 0.3; 
  state.movingAverageElbowAngle = (smoothingFactor * avgElbowAngle) + ((1 - smoothingFactor) * state.movingAverageElbowAngle);
  const smoothedElbowAngle = state.movingAverageElbowAngle;
  
  
  if (state.lastPositions.length >= 10) {
    state.lastPositions.shift();
  }
  
  const currentPosition = {
    shoulderWidth: shoulderWidth,
    elbowWidth: elbowWidth,
    wristElbowYDiff: avgWristElbowYDiff,
    wristShoulderXDiff: avgWristShoulderXDiff,
    elbowAngle: avgElbowAngle,
    timestamp: timestamp
  };
  
  state.lastPositions.push(currentPosition);
  
  
  
  if (state.positionHistory.length >= 30) {
    state.positionHistory.shift();
  }
  state.positionHistory.push(currentPosition);
  
  
  let isExtendedPosition = false;
  let isPulledPosition = false;
  
  if (state.lastPositions.length >= 3) {
    const recent = state.lastPositions.slice(-3);
    
    
    let elbowWidthChange = 0;
    let wristElbowYDiffChange = 0;
    let wristShoulderXDiffChange = 0;
    
    for (let i = 1; i < recent.length; i++) {
      elbowWidthChange += recent[i].elbowWidth - recent[i-1].elbowWidth;
      wristElbowYDiffChange += recent[i].wristElbowYDiff - recent[i-1].wristElbowYDiff;
      wristShoulderXDiffChange += recent[i].wristShoulderXDiff - recent[i-1].wristShoulderXDiff;
    }
    
    
    elbowWidthChange /= (recent.length - 1);
    wristElbowYDiffChange /= (recent.length - 1);
    wristShoulderXDiffChange /= (recent.length - 1);
    
    
    
    
    state.isMovingBack = wristShoulderXDiffChange < -0.005 && elbowWidthChange > 0.002;
    state.isMovingForward = wristShoulderXDiffChange > 0.005 && elbowWidthChange < -0.002;
    
    
    
    
    
    const elbowAngleChanges = recent.map((p, i) => {
      if (i === 0) return 0;
      return p.elbowAngle - recent[i-1].elbowAngle;
    }).slice(1);
    
    
    const avgElbowAngleChange = elbowAngleChanges.reduce((sum, val) => sum + val, 0) / elbowAngleChanges.length;
    
    
    if (avgElbowAngleChange < -1.5) {
      state.isMovingBack = true;
    }
    
    
    if (avgElbowAngleChange > 1.5) {
      state.isMovingForward = true;
    }
    
    
    if (recent.length >= 3) {
      let jitter = 0;
      for (let i = 2; i < recent.length; i++) {
        const prevChange = recent[i-1].wristShoulderXDiff - recent[i-2].wristShoulderXDiff;
        const currChange = recent[i].wristShoulderXDiff - recent[i-1].wristShoulderXDiff;
        if (Math.sign(prevChange) !== Math.sign(currChange)) {
          jitter += Math.abs(currChange);
        }
      }
      
      state.controlScore = Math.max(0, 100 - (jitter * 2000));
    }
  }
  
  
  if (state.previousTimestamp) {
    const timeDelta = (timestamp - state.previousTimestamp) / 1000; 
    if (timeDelta > 0) {
      const wristChange = Math.abs(avgWristShoulderXDiff - (state.previousWristShoulderXDiff || 0));
      const velocity = wristChange / timeDelta;
      
      
      const idealVelocity = 0.2;
      const velocityFactor = 1 - Math.min(1, Math.abs(velocity - idealVelocity) / idealVelocity);
      state.velocityScore = Math.round(velocityFactor * 100);
    }
  }
  
  
  state.previousElbowWidth = elbowWidth;
  state.previousWristElbowYDiff = avgWristElbowYDiff;
  state.previousWristShoulderXDiff = avgWristShoulderXDiff;
  state.previousShoulderWidth = shoulderWidth;
  state.previousTimestamp = timestamp;
  
  
  if (!state.maxWristShoulderXDiff) state.maxWristShoulderXDiff = 0.3; 
  if (!state.minWristShoulderXDiff) state.minWristShoulderXDiff = 0.1;
  if (!state.maxElbowWidth) state.maxElbowWidth = 0.5;
  if (!state.minElbowWidth) state.minElbowWidth = 0.2;
  
  
  if (avgWristShoulderXDiff > state.maxWristShoulderXDiff && avgWristShoulderXDiff < 0.6) {
    state.maxWristShoulderXDiff = avgWristShoulderXDiff;
  }
  if (avgWristShoulderXDiff < state.minWristShoulderXDiff && avgWristShoulderXDiff > 0.05) {
    state.minWristShoulderXDiff = avgWristShoulderXDiff;
  }
  if (elbowWidth > state.maxElbowWidth && elbowWidth < 0.8) {
    state.maxElbowWidth = elbowWidth;
  }
  if (elbowWidth < state.minElbowWidth && elbowWidth > 0.1) {
    state.minElbowWidth = elbowWidth;
  }
  
  
  const wristShoulderXDiffRange = state.maxWristShoulderXDiff - state.minWristShoulderXDiff;
  const elbowWidthRange = state.maxElbowWidth - state.minElbowWidth;
  
  const extendedWristXThreshold = state.maxWristShoulderXDiff - (wristShoulderXDiffRange * 0.3);
  const pulledWristXThreshold = state.minWristShoulderXDiff + (wristShoulderXDiffRange * 0.3);
  const pulledElbowWidthThreshold = state.maxElbowWidth - (elbowWidthRange * 0.3);
  
  
  
  let pcaConfidence = 0;
  if (state.positionHistory.length >= 20) {
    pcaConfidence = calculatePCAConfidence(state.positionHistory);
    
    
    if (pcaConfidence > 0.7) {
      
      
      state.patternConfidence = pcaConfidence;
    }
  }
  
  
  let dtwMatch = false;
  if (state.referencePattern && state.positionHistory.length >= 15) {
    const recentMotion = state.positionHistory.slice(-15);
    const dtwDistance = calculateDTWDistance(recentMotion, state.referencePattern);
    dtwMatch = dtwDistance < 0.3; 
    
    if (dtwMatch) {
      state.patternMatchConfidence = 1 - dtwDistance;
    }
  }
  
  
  
  let thresholdExtendedPosition = avgWristShoulderXDiff > extendedWristXThreshold && 
                           elbowWidth < pulledElbowWidthThreshold &&
                           smoothedElbowAngle > 130;
  
  let thresholdPulledPosition = avgWristShoulderXDiff < pulledWristXThreshold && 
                         elbowWidth > pulledElbowWidthThreshold &&
                         smoothedElbowAngle < 120;
  
  
  
  
  const frontFacingExtendedPosition = 
    avgWristShoulderXDiff > (extendedWristXThreshold * 0.8) && 
    smoothedElbowAngle > 140; 
  
  const frontFacingPulledPosition = 
    (elbowWidth > (state.minElbowWidth + (elbowWidthRange * 0.4))) && 
    smoothedElbowAngle < 130 && 
    
    Math.abs(leftShoulder.y - rightShoulder.y) < 0.06;
  
  
  thresholdExtendedPosition = thresholdExtendedPosition || frontFacingExtendedPosition;
  thresholdPulledPosition = thresholdPulledPosition || frontFacingPulledPosition;
  
  
  
  if (dtwMatch && state.patternMatchConfidence > 0.7) {
    
    if (state.dtwMatchedPhase === "extended") {
      thresholdExtendedPosition = true;
    } else if (state.dtwMatchedPhase === "pulled") {
      thresholdPulledPosition = true;
    }
  }
  
  
  if (pcaConfidence > 0.7) {
    
    
    isExtendedPosition = thresholdExtendedPosition;
    isPulledPosition = thresholdPulledPosition;
  } else {
    
    isExtendedPosition = thresholdExtendedPosition;
    isPulledPosition = thresholdPulledPosition;
  }
  
  
  if (!state.referencePattern && state.hasCompletedFullRep) {
    
    state.referencePattern = state.lastRepPattern;
  }
  
  
  
  
  let elbowBendScore = 100;
  if (isExtendedPosition) {
    
    elbowBendScore = Math.min(100, (smoothedElbowAngle / 170) * 100);
  } else if (isPulledPosition) {
    
    const optimalPullAngle = 90;
    const angleDiff = Math.abs(smoothedElbowAngle - optimalPullAngle);
    elbowBendScore = Math.max(0, 100 - (angleDiff / 1.5));
  }
  
  
  const elbowAngleDiff = Math.abs(leftElbowAngle - rightElbowAngle);
  const wristYDiff = Math.abs(leftWrist.y - rightWrist.y);
  state.symmetryScore = Math.min(100, 100 - (elbowAngleDiff) - (wristYDiff * 100));
  
  
  
  let chestExpansionScore = 100;
  if (isPulledPosition) {
    const optimalShoulderWidthIncrease = 0.05; 
    const actualWidth = shoulderWidth - (state.minShoulderWidth || shoulderWidth);
    chestExpansionScore = Math.max(0, 100 - (Math.abs(actualWidth - optimalShoulderWidthIncrease) * 500));
  }
  
  
  if (!state.minShoulderWidth || shoulderWidth < state.minShoulderWidth) {
    state.minShoulderWidth = shoulderWidth;
  }
  if (!state.maxShoulderWidth || shoulderWidth > state.maxShoulderWidth) {
    state.maxShoulderWidth = shoulderWidth;
  }
  
  
  state.elbowBendScore = elbowBendScore;
  state.rowAngle = smoothedElbowAngle;
  state.chestExpansionScore = chestExpansionScore;
  
  
  let formFeedback = null;
  
  
  if (state.symmetryScore < 70 && isPulledPosition) {
    formFeedback = "Pull evenly with both arms";
  }
  
  else if (isPulledPosition && smoothedElbowAngle > 120) {
    formFeedback = "Bend elbows more during the row";
  }
  
  else if (chestExpansionScore < 70 && isPulledPosition) {
    formFeedback = "Pull shoulders back during the row";
  }
  
  else if (state.controlScore < 70) {
    formFeedback = "Row with smoother, more controlled motion";
  }
  
  else {
    const shoulderElevation = Math.abs(leftShoulder.y - rightShoulder.y);
    if (shoulderElevation > 0.08) {
      formFeedback = "Keep shoulders level during row";
    }
  }
  
  
  
  
  
  
  if (!state.lastCountedPullTime) state.lastCountedPullTime = 0;
  if (!state.consecutiveExtendedFrames) state.consecutiveExtendedFrames = 0;
  if (!state.consecutivePulledFrames) state.consecutivePulledFrames = 0;
  if (!state.inActiveRow) state.inActiveRow = true; 
  if (state.hasVisitedExtendedPosition === undefined) state.hasVisitedExtendedPosition = false;
  
  
  console.log(`ROWING DEBUG: Extended=${isExtendedPosition}, Pulled=${isPulledPosition}, Elbow Angle=${smoothedElbowAngle.toFixed(1)}, Width=${elbowWidth.toFixed(2)}, WristDiff=${avgWristShoulderXDiff.toFixed(2)}`);
  
  
  const veryLenientExtendedPosition = smoothedElbowAngle > 120;
  const veryLenientPulledPosition = (
    (elbowWidth > (state.minElbowWidth + (elbowWidthRange * 0.3))) && 
    smoothedElbowAngle < 140
  );
  
  
  const positionForTracking = veryLenientExtendedPosition ? 'extended' : 
                             (veryLenientPulledPosition ? 'pulled' : 'neutral');
  
  
  console.log(`ROWING DEBUG: Current position: ${positionForTracking}, 
    Consecutive extended=${state.consecutiveExtendedFrames}, 
    Consecutive pulled=${state.consecutivePulledFrames}, 
    Active=${state.inActiveRow},
    Extended position visited=${state.hasVisitedExtendedPosition}`);
  
  
  if (veryLenientExtendedPosition) {
    state.consecutiveExtendedFrames++;
    state.consecutivePulledFrames = Math.max(0, state.consecutivePulledFrames - 1);
  } else if (veryLenientPulledPosition) {
    state.consecutivePulledFrames++;
    state.consecutiveExtendedFrames = Math.max(0, state.consecutiveExtendedFrames - 1);
  } else {
    
    state.consecutiveExtendedFrames = Math.max(0, state.consecutiveExtendedFrames - 1);
    state.consecutivePulledFrames = Math.max(0, state.consecutivePulledFrames - 1);
  }
  
  
  
  const stableExtendedPosition = state.consecutiveExtendedFrames >= 2;
  const stablePulledPosition = state.consecutivePulledFrames >= 2;
  
  
  if (!state.hasVisitedExtendedPosition && stableExtendedPosition) {
    state.hasVisitedExtendedPosition = true;
  }
  
  
  state.inActiveRow = true;
  
  let repCompleted = false;
  
  
  
  if (stablePulledPosition && state.hasVisitedExtendedPosition && (timestamp - state.lastCountedPullTime > 800)) {
    
    
    state.lastCountedPullTime = timestamp;
    
    state.hasVisitedExtendedPosition = false;
    
    
    const patternBonus = state.patternMatchConfidence ? state.patternMatchConfidence * 10 : 0;
    
    state.repFormScore = Math.round(
      (state.elbowBendScore * 0.25) + 
      (state.symmetryScore * 0.25) + 
      (state.controlScore * 0.2) +
      (state.chestExpansionScore * 0.2) +
      patternBonus
    );
    
    
    state.formScore = state.repFormScore;
    state.hasCompletedFullRep = true;
    
    
    repCompleted = true;
    console.log("ROWING: Rep counted!");
    console.log("ROWING DEBUG: Rep counted - resetting extended position visit flag");
    
    
    if (!state.referencePattern && state.positionHistory.length >= 15) {
      state.lastRepPattern = state.positionHistory.slice(-15);
      state.referencePattern = state.lastRepPattern;
      console.log("ROWING: Stored reference pattern");
    }
  }
  
  
  if (state.repFormScore) {
    
    state.formScore = Math.round(state.formScore * 0.9 + state.repFormScore * 0.1);
  } else {
    
    const patternBonus = state.patternMatchConfidence ? state.patternMatchConfidence * 10 : 0;
    
    const currentFormScore = Math.round(
      (state.elbowBendScore * 0.25) + 
      (state.symmetryScore * 0.25) + 
      (state.controlScore * 0.2) +
      (state.chestExpansionScore * 0.2) +
      patternBonus
    );
    state.formScore = Math.round(state.formScore * 0.9 + currentFormScore * 0.1);
  }
  
  return { isRepCompleted: repCompleted, formFeedback, exerciseState: state };
}


function applyKalmanFilter(filter, measurement) {
  
  const prediction = filter.x;
  const errorPrediction = filter.p + filter.q;
  
  
  const gain = errorPrediction / (errorPrediction + filter.r);
  const estimate = prediction + gain * (measurement - prediction);
  const errorEstimate = (1 - gain) * errorPrediction;
  
  
  filter.x = estimate;
  filter.p = errorEstimate;
  
  return estimate;
}


function calculatePCAConfidence(positionHistory) {
  if (positionHistory.length < 10) return 0;
  
  
  
  
  
  const wristDiffs = positionHistory.map(p => p.wristShoulderXDiff);
  const elbowWidths = positionHistory.map(p => p.elbowWidth);
  
  
  let patternScore = 0;
  let directionChanges = 0;
  
  
  for (let i = 2; i < wristDiffs.length; i++) {
    const prevDiff = wristDiffs[i-1] - wristDiffs[i-2];
    const currDiff = wristDiffs[i] - wristDiffs[i-1];
    if (Math.sign(prevDiff) !== Math.sign(currDiff)) {
      directionChanges++;
    }
  }
  
  
  
  const idealChanges = positionHistory.length / 5; 
  patternScore = 1 - Math.min(1, Math.abs(directionChanges - idealChanges) / idealChanges);
  
  
  
  let correlation = 0;
  
  
  let sumProduct = 0;
  let sumWrist = 0;
  let sumElbow = 0;
  let sumWristSq = 0;
  let sumElbowSq = 0;
  
  for (let i = 0; i < wristDiffs.length; i++) {
    sumProduct += wristDiffs[i] * elbowWidths[i];
    sumWrist += wristDiffs[i];
    sumElbow += elbowWidths[i];
    sumWristSq += wristDiffs[i] * wristDiffs[i];
    sumElbowSq += elbowWidths[i] * elbowWidths[i];
  }
  
  const n = wristDiffs.length;
  const numerator = n * sumProduct - sumWrist * sumElbow;
  const denominator = Math.sqrt((n * sumWristSq - sumWrist * sumWrist) * (n * sumElbowSq - sumElbow * sumElbow));
  
  if (denominator !== 0) {
    correlation = -numerator / denominator; 
  }
  
  
  const correlationScore = correlation > 0 ? 0 : Math.min(1, -correlation);
  
  
  return (patternScore * 0.6) + (correlationScore * 0.4);
}


function calculateDTWDistance(testPattern, referencePattern) {
  if (!testPattern || !referencePattern || testPattern.length < 5 || referencePattern.length < 5) {
    return 1.0; 
  }
  
  
  
  
  
  
  
  
  const normalizedTest = resamplePattern(testPattern, 10);
  const normalizedReference = resamplePattern(referencePattern, 10);
  
  
  let totalDistance = 0;
  let maxDistance = 0;
  
  
  for (let i = 0; i < normalizedTest.length; i++) {
    const testPos = normalizedTest[i];
    const refPos = normalizedReference[i];
    
    
    const wristDiff = Math.abs(testPos.wristShoulderXDiff - refPos.wristShoulderXDiff);
    const elbowDiff = Math.abs(testPos.elbowWidth - refPos.elbowWidth);
    const angleDiff = Math.abs(testPos.elbowAngle - refPos.elbowAngle) / 180; 
    
    
    const pointDistance = (wristDiff * 0.4) + (elbowDiff * 0.3) + (angleDiff * 0.3);
    totalDistance += pointDistance;
    maxDistance += 1; 
  }
  
  
  return totalDistance / maxDistance;
}


function resamplePattern(pattern, targetLength) {
  if (pattern.length === 0) return [];
  if (pattern.length === targetLength) return pattern;
  
  const result = [];
  const stepSize = (pattern.length - 1) / (targetLength - 1);
  
  for (let i = 0; i < targetLength; i++) {
    const index = Math.min(pattern.length - 1, Math.floor(i * stepSize));
    result.push(pattern[index]);
  }
  
  return result;
}


function calculateDistance3D(point1, point2) {
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;
  const dz = point2.z - point1.z;
  return Math.sqrt(dx*dx + dy*dy + dz*dz);
}


function analyzeBicepCurl(results, timestamp) {
  const pose = results.landmarks[0];
  const worldPose = results.worldLandmarks[0];
  const state = exerciseStates[ExerciseType.BICEP_CURL];
  
  
  if (!worldPose || !worldPose[11] || !worldPose[12] || !worldPose[13] || !worldPose[14] || !worldPose[15] || !worldPose[16]) {
    return { isRepCompleted: false, formFeedback: null, exerciseState: state };
  }
  
  const leftShoulder = worldPose[11];
  const rightShoulder = worldPose[12];
  const leftElbow = worldPose[13];
  const rightElbow = worldPose[14];
  const leftWrist = worldPose[15];
  const rightWrist = worldPose[16];
  
  
  const activeSide = state.activeSide || 'right';
  const activeShoulder = activeSide === 'right' ? rightShoulder : leftShoulder;
  const activeElbow = activeSide === 'right' ? rightElbow : leftElbow;
  const activeWrist = activeSide === 'right' ? rightWrist : leftWrist;
  
  
  const elbowAngle = calculateJointAngle(activeShoulder, activeElbow, activeWrist);
  state.curlAngle = elbowAngle;
  
  
  if (state.previousTimestamp) {
    const angleDifference = elbowAngle - (state.curlAngle || elbowAngle);
    
    state.isMovingUp = angleDifference < -0.5; 
  } else {
    
    state.isMovingUp = false;
  }
  
  state.previousWristPosition = { ...activeWrist };
  state.previousTimestamp = timestamp;
  
  
  
  
  
  
  const extendedThreshold = activeSide === 'right' ? 130 : 110; 
  const curledThreshold = activeSide === 'right' ? 100 : 80;    
  
  const isExtendedPosition = elbowAngle > extendedThreshold;
  
  
  const isCurledPosition = elbowAngle < curledThreshold;
  
  
  
  
  
  
  state.extensionScore = Math.min(100, (elbowAngle / 170) * 100);
  
  
  state.contractionScore = Math.min(100, 100 - ((elbowAngle / 90) * 100));
  
  
  
  const stableElbowThreshold = 0.05;
  const shoulderToElbowX = Math.abs(activeShoulder.x - activeElbow.x);
  
  
  let elbowStabilityScore = 100;
  
  
  if (state.previousElbowPosition) {
    const elbowMovement = Math.abs(state.previousElbowPosition.x - activeElbow.x);
    elbowStabilityScore = Math.max(0, 100 - (elbowMovement * 500));
  }
  
  state.previousElbowPosition = { ...activeElbow };
  state.elbowStabilityScore = elbowStabilityScore;
  
  
  let formFeedback = null;
  
  
  if (activeSide === 'left') {
    formFeedback = "TRACKING LEFT ARM";
  } else {
    formFeedback = "TRACKING RIGHT ARM";
  }
  
  
  if (elbowStabilityScore < 70 && (state.isMovingUp || isCurledPosition)) {
    formFeedback = `${activeSide === 'left' ? 'L: ' : 'R: '}Keep your elbow in position`;
  }
  
  
  else if (isExtendedPosition && elbowAngle < 160) {
    formFeedback = `${activeSide === 'left' ? 'L: ' : 'R: '}Extend arm fully at bottom`;
  }
  
  
  else if (isCurledPosition && elbowAngle > 70) {
    formFeedback = `${activeSide === 'left' ? 'L: ' : 'R: '}Curl higher for full contraction`;
  }
  
  
  let repCompleted = false;
  
  
  if (isExtendedPosition && !state.isInExtendedPosition) {
    state.lastExtendedTime = timestamp;
    state.isInExtendedPosition = true;
    state.hasCompletedCurlPosition = false;
  }
  
  
  if (isCurledPosition && state.isInExtendedPosition && 
      timestamp - state.lastExtendedTime > 200) {
    
    state.lastCurledTime = timestamp;
    state.isInExtendedPosition = false;
    state.hasCompletedCurlPosition = true;
    state.maxCurlAngle = Math.min(state.maxCurlAngle || 90, elbowAngle); 
    
    
    state.repFormScore = Math.round(
      (state.extensionScore * 0.3) + 
      (state.contractionScore * 0.4) + 
      (state.elbowStabilityScore * 0.3)
    );
    
    
    state.formScore = state.repFormScore;
    
    repCompleted = true;
  }
  
  
  if (isExtendedPosition && !state.isInExtendedPosition && state.hasCompletedCurlPosition &&
      timestamp - state.lastCurledTime > 300) {
    state.isInExtendedPosition = true;
    state.hasCompletedCurlPosition = false;
    state.lastExtendedTime = timestamp;
    
    
    
  }
  
  
  if (!state.isInExtendedPosition && timestamp - state.lastCurledTime > 5000) {
    state.isInExtendedPosition = true;
    state.hasCompletedCurlPosition = false;
  }
  
  
  if (state.repFormScore) {
    state.formScore = Math.round(state.formScore * 0.9 + state.repFormScore * 0.1);
  } else {
    const currentFormScore = Math.round(
      (state.extensionScore * 0.3) + 
      (state.contractionScore * 0.4) + 
      (state.elbowStabilityScore * 0.3)
    );
    state.formScore = Math.round(state.formScore * 0.9 + currentFormScore * 0.1);
  }
  
  return { isRepCompleted: repCompleted, formFeedback, exerciseState: state };
}


function analyzeSeatedKneeExtensions(results, timestamp) {
  const pose = results.landmarks[0];
  const worldPose = results.worldLandmarks[0];
  const state = exerciseStates[ExerciseType.SEATED_KNEE_EXTENSIONS];
  
  
  if (!worldPose || !worldPose[23] || !worldPose[24] || !worldPose[25] || !worldPose[26] || !worldPose[27] || !worldPose[28]) {
    return { isRepCompleted: false, formFeedback: null, exerciseState: state };
  }
  
  const leftHip = worldPose[23];
  const rightHip = worldPose[24];
  const leftKnee = worldPose[25];
  const rightKnee = worldPose[26];
  const leftAnkle = worldPose[27];
  const rightAnkle = worldPose[28];
  
  
  const activeSide = state.activeLeg || 'right';
  const activeHip = activeSide === 'right' ? rightHip : leftHip;
  const activeKnee = activeSide === 'right' ? rightKnee : leftKnee;
  const activeAnkle = activeSide === 'right' ? rightAnkle : leftAnkle;
  
  
  const kneeAngle = calculateJointAngle(activeHip, activeKnee, activeAnkle);
  state.extensionAngle = kneeAngle;
  
  
  if (state.previousTimestamp) {
    const angleDifference = kneeAngle - (state.extensionAngle || kneeAngle);
    state.isMovingForward = angleDifference > 1; 
  }
  
  state.previousAnklePosition = { ...activeAnkle };
  state.previousTimestamp = timestamp;
  
  
  
  const isFlexedPosition = kneeAngle < 100;
  
  
  const isExtendedPosition = kneeAngle > 160;
  
  
  
  
  state.extensionScore = Math.min(100, (kneeAngle / 180) * 100);
  
  
  let hipStabilityScore = 100;
  
  
  if (state.previousHipPosition) {
    const hipMovement = calculateDistance3D(state.previousHipPosition, activeHip);
    hipStabilityScore = Math.max(0, 100 - (hipMovement * 500));
  }
  
  state.previousHipPosition = { ...activeHip };
  state.hipStabilityScore = hipStabilityScore;
  
  
  let controlScore = 100;
  if (state.isMovingForward && state.lastAngles) {
    
    
    const angleChanges = [];
    for (let i = 1; i < state.lastAngles.length; i++) {
      angleChanges.push(state.lastAngles[i] - state.lastAngles[i-1]);
    }
    
    
    let variance = 0;
    if (angleChanges.length > 1) {
      const mean = angleChanges.reduce((a, b) => a + b, 0) / angleChanges.length;
      variance = angleChanges.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b, 0) / angleChanges.length;
      controlScore = Math.max(0, 100 - (variance * 10));
    }
  }
  
  
  if (!state.lastAngles) state.lastAngles = [];
  if (state.lastAngles.length > 5) state.lastAngles.shift();
  state.lastAngles.push(kneeAngle);
  
  state.controlScore = controlScore;
  
  
  let formFeedback = null;
  
  
  if (hipStabilityScore < 70) {
    formFeedback = "Keep hips stable during extension";
  }
  
  
  else if (isExtendedPosition && kneeAngle < 170) {
    formFeedback = "Extend leg more fully";
  }
  
  
  else if (controlScore < 70 && state.isMovingForward) {
    formFeedback = "Extend more smoothly for better control";
  }
  
  
  let repCompleted = false;
  
  
  if (isFlexedPosition && !state.isInFlexedPosition) {
    state.lastFlexedTime = timestamp;
    state.isInFlexedPosition = true;
    state.hasCompletedExtension = false;
  }
  
  
  if (isExtendedPosition && state.isInFlexedPosition && 
      state.isMovingForward && timestamp - state.lastFlexedTime > 200) {
    
    state.lastExtendedTime = timestamp;
    state.isInFlexedPosition = false;
    state.hasCompletedExtension = true;
    state.maxExtensionAngle = Math.max(state.maxExtensionAngle || 0, kneeAngle); 
    
    
    state.repFormScore = Math.round(
      (state.extensionScore * 0.5) + 
      (state.hipStabilityScore * 0.3) + 
      (state.controlScore * 0.2)
    );
    
    
    state.formScore = state.repFormScore;
    
    repCompleted = true;
  }
  
  
  if (isFlexedPosition && !state.isInFlexedPosition && state.hasCompletedExtension &&
      timestamp - state.lastExtendedTime > 300) {
    state.isInFlexedPosition = true;
    state.hasCompletedExtension = false;
    state.lastFlexedTime = timestamp;
    
    
    state.activeLeg = activeSide === 'right' ? 'left' : 'right';
  }
  
  
  if (!state.isInFlexedPosition && timestamp - state.lastExtendedTime > 5000) {
    state.isInFlexedPosition = true;
    state.hasCompletedExtension = false;
  }
  
  
  if (state.repFormScore) {
    state.formScore = Math.round(state.formScore * 0.9 + state.repFormScore * 0.1);
  } else {
    const currentFormScore = Math.round(
      (state.extensionScore * 0.5) + 
      (state.hipStabilityScore * 0.3) + 
      (state.controlScore * 0.2)
    );
    state.formScore = Math.round(state.formScore * 0.9 + currentFormScore * 0.1);
  }
  
  return { isRepCompleted: repCompleted, formFeedback, exerciseState: state };
}


function analyzeSeatedMarches(results, timestamp) {
  const pose = results.landmarks[0];
  const worldPose = results.worldLandmarks[0];
  const state = exerciseStates[ExerciseType.SEATED_MARCHES];
  
  
  if (!worldPose || !worldPose[23] || !worldPose[24] || !worldPose[25] || !worldPose[26] || !worldPose[27] || !worldPose[28]) {
    return { isRepCompleted: false, formFeedback: null, exerciseState: state };
  }
  
  const leftHip = worldPose[23];
  const rightHip = worldPose[24];
  const leftKnee = worldPose[25];
  const rightKnee = worldPose[26];
  const leftAnkle = worldPose[27];
  const rightAnkle = worldPose[28];
  
  
  const activeSide = state.activeLeg || 'right';
  const activeHip = activeSide === 'right' ? rightHip : leftHip;
  const activeKnee = activeSide === 'right' ? rightKnee : leftKnee;
  const activeAnkle = activeSide === 'right' ? rightAnkle : leftAnkle;
  
  const inactiveHip = activeSide === 'right' ? leftHip : rightHip;
  const inactiveKnee = activeSide === 'right' ? leftKnee : rightKnee;
  const inactiveAnkle = activeSide === 'right' ? leftAnkle : rightAnkle;
  
  
  
  
  const kneeRaiseHeight = activeHip.y - activeKnee.y;
  state.raiseHeight = kneeRaiseHeight;
  
  
  const hipKneeAngle = calculateJointAngle(activeHip, activeKnee, activeAnkle);
  
  
  if (state.previousTimestamp) {
    const heightDifference = kneeRaiseHeight - (state.raiseHeight || 0);
    state.isMovingUp = heightDifference > 0.005; 
  }
  
  state.previousKneePosition = { ...activeKnee };
  state.previousTimestamp = timestamp;
  
  
  
  const isInDownPosition = kneeRaiseHeight < 0.05 && hipKneeAngle > 140;
  
  
  const isInRaisedPosition = kneeRaiseHeight > 0.15 && hipKneeAngle < 120;
  
  
  
  
  const optimalRaiseHeight = 0.25; 
  state.heightScore = Math.min(100, (kneeRaiseHeight / optimalRaiseHeight) * 100);
  
  
  
  let balanceScore = 100;
  
  
  if (state.previousInactiveKneePosition) {
    
    const inactiveMovement = calculateDistance3D(state.previousInactiveKneePosition, inactiveKnee);
    balanceScore = Math.max(0, 100 - (inactiveMovement * 500));
  }
  
  state.previousInactiveKneePosition = { ...inactiveKnee };
  state.balanceScore = balanceScore;
  
  
  let controlScore = 100;
  
  
  if (state.lastHeights && state.lastHeights.length >= 3) {
    
    const heightChanges = [];
    for (let i = 1; i < state.lastHeights.length; i++) {
      heightChanges.push(state.lastHeights[i] - state.lastHeights[i-1]);
    }
    
    
    let variance = 0;
    if (heightChanges.length > 1) {
      const mean = heightChanges.reduce((a, b) => a + b, 0) / heightChanges.length;
      variance = heightChanges.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b, 0) / heightChanges.length;
      controlScore = Math.max(0, 100 - (variance * 1000));
    }
  }
  
  
  if (!state.lastHeights) state.lastHeights = [];
  if (state.lastHeights.length > 5) state.lastHeights.shift();
  state.lastHeights.push(kneeRaiseHeight);
  
  state.controlScore = controlScore;
  
  
  let formFeedback = null;
  
  
  if (state.heightScore < 60 && isInRaisedPosition) {
    formFeedback = "Raise knee higher for better range of motion";
  }
  
  
  else if (balanceScore < 70) {
    formFeedback = "Keep inactive leg stable for better balance";
  }
  
  
  else if (controlScore < 70 && state.isMovingUp) {
    formFeedback = "Move more smoothly for better control";
  }
  
  
  const hipAlignment = Math.abs(leftHip.y - rightHip.y);
  if (hipAlignment > 0.05) {
    formFeedback = "Keep hips level during marches";
  }
  
  
  let repCompleted = false;
  
  
  if (isInDownPosition && !state.isInDownPosition) {
    state.lastDownTime = timestamp;
    state.isInDownPosition = true;
    state.hasCompletedRaisePosition = false;
  }
  
  
  if (isInRaisedPosition && state.isInDownPosition && 
      state.isMovingUp && timestamp - state.lastDownTime > 200) {
    
    state.lastRaisedTime = timestamp;
    state.isInDownPosition = false;
    state.hasCompletedRaisePosition = true;
    state.maxRaiseHeight = Math.max(state.maxRaiseHeight || 0, kneeRaiseHeight); 
    
    
    state.repFormScore = Math.round(
      (state.heightScore * 0.5) + 
      (state.balanceScore * 0.3) + 
      (state.controlScore * 0.2)
    );
    
    
    state.formScore = state.repFormScore;
    
    repCompleted = true;
  }
  
  
  if (isInDownPosition && !state.isInDownPosition && state.hasCompletedRaisePosition &&
      timestamp - state.lastRaisedTime > 300) {
    state.isInDownPosition = true;
    state.hasCompletedRaisePosition = false;
    state.lastDownTime = timestamp;
    
    
    state.activeLeg = activeSide === 'right' ? 'left' : 'right';
  }
  
  
  if (!state.isInDownPosition && timestamp - state.lastRaisedTime > 5000) {
    state.isInDownPosition = true;
    state.hasCompletedRaisePosition = false;
  }
  
  
  if (state.repFormScore) {
    state.formScore = Math.round(state.formScore * 0.9 + state.repFormScore * 0.1);
  } else {
    const currentFormScore = Math.round(
      (state.heightScore * 0.5) + 
      (state.balanceScore * 0.3) + 
      (state.controlScore * 0.2)
    );
    state.formScore = Math.round(state.formScore * 0.9 + currentFormScore * 0.1);
  }
  
  return { isRepCompleted: repCompleted, formFeedback, exerciseState: state };
}


function analyzeShoulderElevation(results, timestamp) {
  const pose = results.landmarks[0];
  const worldPose = results.worldLandmarks[0];
  const state = exerciseStates[ExerciseType.SHOULDER_ELEVATION];
  
  
  if (!worldPose || !worldPose[0] || !worldPose[11] || !worldPose[12]) {
    return { isRepCompleted: false, formFeedback: null, exerciseState: state };
  }
  
  const nose = worldPose[0];
  const leftShoulder = worldPose[11];
  const rightShoulder = worldPose[12];
  
  
  if (!state.lastPositions) state.lastPositions = [];
  
  
  const avgShoulderY = (leftShoulder.y + rightShoulder.y) / 2;
  
  
  
  const shoulderToNoseDistance = nose.y - avgShoulderY;
  
  
  if (state.lastPositions.length >= 5) {
    state.lastPositions.shift();
  }
  state.lastPositions.push({
    distance: shoulderToNoseDistance,
    leftY: leftShoulder.y,
    rightY: rightShoulder.y,
    timestamp
  });
  
  
  let isMovingUp = false;
  if (state.lastPositions.length >= 2) {
    const current = state.lastPositions[state.lastPositions.length - 1];
    const previous = state.lastPositions[state.lastPositions.length - 2];
    isMovingUp = current.distance < previous.distance; 
  }
  state.isMovingUp = isMovingUp;
  
  
  let velocityScore = 100;
  if (state.lastPositions.length >= 3) {
    const current = state.lastPositions[state.lastPositions.length - 1];
    const previous = state.lastPositions[0];
    const timeDelta = (current.timestamp - previous.timestamp) / 1000; 
    if (timeDelta > 0) {
      const distanceDelta = Math.abs(current.distance - previous.distance);
      const velocity = distanceDelta / timeDelta;
      
      const optimalVelocity = 0.1; 
      velocityScore = Math.max(0, 100 - Math.abs(velocity - optimalVelocity) * 500);
    }
  }
  
  
  
  if (!state.relaxedShoulderDistance && shoulderToNoseDistance > 0.3) {
    state.relaxedShoulderDistance = shoulderToNoseDistance;
  }
  
  const baselineDistance = state.relaxedShoulderDistance || 0.35; 
  
  
  const isInDownPosition = shoulderToNoseDistance > baselineDistance - 0.02;
  
  
  const isInElevatedPosition = shoulderToNoseDistance < baselineDistance - 0.05;
  
  
  
  
  const elevationAmount = Math.max(0, baselineDistance - shoulderToNoseDistance);
  const optimalElevation = 0.08; 
  state.heightScore = Math.min(100, (elevationAmount / optimalElevation) * 100);
  
  
  const symmetryDifference = Math.abs(leftShoulder.y - rightShoulder.y);
  state.symmetryScore = Math.max(0, 100 - (symmetryDifference * 1000));
  
  
  state.controlScore = velocityScore;
  
  
  state.elevationHeight = elevationAmount;
  
  
  let formFeedback = null;
  
  
  if (state.symmetryScore < 80 && isInElevatedPosition) {
    formFeedback = "Elevate both shoulders evenly";
  }
  
  
  else if (state.heightScore < 60 && isInElevatedPosition) {
    formFeedback = "Raise shoulders higher for better activation";
  }
  
  
  else if (state.controlScore < 70) {
    formFeedback = "Move more smoothly and controlled";
  }
  
  
  let repCompleted = false;
  
  
  if (isInDownPosition && !state.isInDownPosition) {
    state.lastDownTime = timestamp;
    state.isInDownPosition = true;
    state.hasCompletedElevatedPosition = false;
  }
  
  
  if (isInElevatedPosition && state.isInDownPosition && 
      state.isMovingUp && timestamp - state.lastDownTime > 200) {
    
    state.lastElevatedTime = timestamp;
    state.isInDownPosition = false;
    state.hasCompletedElevatedPosition = true;
    state.maxElevationHeight = Math.max(state.maxElevationHeight || 0, elevationAmount); 
    
    
    state.repFormScore = Math.round(
      (state.heightScore * 0.4) + 
      (state.symmetryScore * 0.4) + 
      (state.controlScore * 0.2)
    );
    
    
    state.formScore = state.repFormScore;
    
    repCompleted = true;
  }
  
  
  if (isInDownPosition && !state.isInDownPosition && state.hasCompletedElevatedPosition &&
      timestamp - state.lastElevatedTime > 300) {
    state.isInDownPosition = true;
    state.hasCompletedElevatedPosition = false;
    state.lastDownTime = timestamp;
  }
  
  
  if (!state.isInDownPosition && timestamp - state.lastElevatedTime > 5000) {
    state.isInDownPosition = true;
    state.hasCompletedElevatedPosition = false;
  }
  
  
  if (state.repFormScore) {
    state.formScore = Math.round(state.formScore * 0.9 + state.repFormScore * 0.1);
  } else {
    const currentFormScore = Math.round(
      (state.heightScore * 0.4) + 
      (state.symmetryScore * 0.4) + 
      (state.controlScore * 0.2)
    );
    state.formScore = Math.round(state.formScore * 0.9 + currentFormScore * 0.1);
  }
  
  return { isRepCompleted: repCompleted, formFeedback, exerciseState: state };
}


function analyzeArmAbductionFront(results, timestamp) {
  const pose = results.landmarks[0];
  const worldPose = results.worldLandmarks[0];
  const state = exerciseStates[ExerciseType.ARM_ABDUCTION_FRONT];
  
  // Check for required landmarks
  if (!worldPose || !worldPose[11] || !worldPose[12] || !worldPose[13] || !worldPose[14] || !worldPose[15] || !worldPose[16]) {
    return { isRepCompleted: false, formFeedback: null, exerciseState: state };
  }
  
  const leftShoulder = worldPose[11];
  const rightShoulder = worldPose[12];
  const leftElbow = worldPose[13];
  const rightElbow = worldPose[14];
  const leftWrist = worldPose[15];
  const rightWrist = worldPose[16];
  
  // Initialize lastPositions array if needed
  if (!state.lastPositions) state.lastPositions = [];
  
  // FIXED: Calculate angle using more reliable y-coordinates for front-facing abduction
  // Use Y position relative to shoulders for better front view detection
  const leftWristShoulderYDiff = leftShoulder.y - leftWrist.y;
  const rightWristShoulderYDiff = rightShoulder.y - rightWrist.y;
  
  // Calculate normalized height (percentage of arm length raised)
  const leftArmLength = calculateDistance3D(leftShoulder, leftWrist);
  const rightArmLength = calculateDistance3D(rightShoulder, rightWrist);
  
  const leftRaiseRatio = leftWristShoulderYDiff / Math.max(0.1, leftArmLength);
  const rightRaiseRatio = rightWristShoulderYDiff / Math.max(0.1, rightArmLength);
  
  // Convert to angle approximation (0-90 degrees)
  const leftArmAngle = leftRaiseRatio * 90;
  const rightArmAngle = rightRaiseRatio * 90;
  
  // Average angle
  const avgAngle = (leftArmAngle + rightArmAngle) / 2;
  state.abductionAngle = avgAngle;
  
  // Debug logging
  console.log(`ARM ABDUCTION FRONT: avgAngle=${avgAngle.toFixed(1)}, leftAngle=${leftArmAngle.toFixed(1)}, rightAngle=${rightArmAngle.toFixed(1)}`);
  
  // Save positions history
  if (state.lastPositions.length >= 5) {
    state.lastPositions.shift();
  }
  state.lastPositions.push({
    angle: avgAngle,
    leftWrist: { ...leftWrist },
    rightWrist: { ...rightWrist },
    timestamp
  });
  
  // Detect movement direction and calculate velocity
  let isMovingUp = false;
  let velocityScore = 100;
  
  if (state.lastPositions.length >= 2) {
    const current = state.lastPositions[state.lastPositions.length - 1];
    const previous = state.lastPositions[state.lastPositions.length - 2];
    isMovingUp = current.angle > previous.angle; 
    
    // Calculate velocity for control score
    if (state.lastPositions.length >= 3) {
      const first = state.lastPositions[0];
      const timeDelta = (current.timestamp - first.timestamp) / 1000; 
      if (timeDelta > 0) {
        const angleDelta = Math.abs(current.angle - first.angle);
        const velocity = angleDelta / timeDelta;
        
        const optimalVelocity = 45; 
        velocityScore = Math.max(0, 100 - Math.abs(velocity - optimalVelocity) * 0.5);
      }
    }
  }
  
  state.isMovingUp = isMovingUp;
  
  // FIXED: Adjusted thresholds to be more lenient
  // Position detection with more lenient thresholds
  const isInDownPosition = avgAngle < 15;  // Was 20
  const isInUpPosition = avgAngle > 60;    // Was 80
  
  // Calculate scores
  const optimalAbductionAngle = 90; 
  state.heightScore = Math.min(100, (avgAngle / optimalAbductionAngle) * 100);
  
  const angleDifference = Math.abs(leftArmAngle - rightArmAngle);
  state.symmetryScore = Math.max(0, 100 - (angleDifference));
  
  const leftElbowStraightness = calculateJointAngle(leftShoulder, leftElbow, leftWrist);
  const rightElbowStraightness = calculateJointAngle(rightShoulder, rightElbow, rightWrist);
  const avgElbowStraightness = (leftElbowStraightness + rightElbowStraightness) / 2;
  state.extensionScore = Math.min(100, (avgElbowStraightness / 180) * 100);
  
  state.controlScore = velocityScore;
  
  // Form feedback
  let formFeedback = null;
  
  if (state.symmetryScore < 80 && isInUpPosition) {
    formFeedback = "Raise both arms evenly";
  }
  else if (state.extensionScore < 80 && isInUpPosition) {
    formFeedback = "Keep arms straighter during abduction";
  }
  else if (state.heightScore < 80 && isInUpPosition) {
    formFeedback = "Raise arms higher for full range of motion";
  }
  
  // Repetition detection
  let repCompleted = false;
  
  // FIXED: Simplified rep detection logic
  // Transition to down position
  if (isInDownPosition && !state.isInDownPosition) {
    state.lastDownTime = timestamp;
    state.isInDownPosition = true;
    state.hasCompletedUpPosition = false;
  }
  
  // Detect completed rep when reaching up position
  // Simplified rep detection - removed isMovingUp requirement
  if (isInUpPosition && state.isInDownPosition && timestamp - state.lastDownTime > 200) {
    state.lastUpTime = timestamp;
    state.isInDownPosition = false;
    state.hasCompletedUpPosition = true;
    state.maxAbductionAngle = Math.max(state.maxAbductionAngle || 0, avgAngle);
    
    state.repFormScore = Math.round(
      (state.heightScore * 0.3) + 
      (state.symmetryScore * 0.3) + 
      (state.extensionScore * 0.3) +
      (state.controlScore * 0.1)
    );
    
    state.formScore = state.repFormScore;
    
    repCompleted = true;
    console.log("ARM ABDUCTION FRONT: Rep detected!");
  }
  
  // Return to down position
  if (isInDownPosition && !state.isInDownPosition && state.hasCompletedUpPosition &&
      timestamp - state.lastUpTime > 300) {
    state.isInDownPosition = true;
    state.hasCompletedUpPosition = false;
    state.lastDownTime = timestamp;
  }
  
  // Timeout safety
  if (!state.isInDownPosition && timestamp - state.lastUpTime > 5000) {
    state.isInDownPosition = true;
    state.hasCompletedUpPosition = false;
  }
  
  // Update form score
  if (state.repFormScore) {
    state.formScore = Math.round(state.formScore * 0.9 + state.repFormScore * 0.1);
  } else {
    const currentFormScore = Math.round(
      (state.heightScore * 0.3) + 
      (state.symmetryScore * 0.3) + 
      (state.extensionScore * 0.3) +
      (state.controlScore * 0.1)
    );
    state.formScore = Math.round(state.formScore * 0.9 + currentFormScore * 0.1);
  }
  
  return { isRepCompleted: repCompleted, formFeedback, exerciseState: state };
}


export function setBicepCurlActiveSide(side) {
  const exerciseState = exerciseStates[ExerciseType.BICEP_CURL];
  exerciseState.activeSide = side;
  
  // Also update FSM if implemented
  setBicepCurlFSMActiveSide(side);
}

export function setCrossBodyPunchActiveSide(side) {
  const exerciseState = exerciseStates[ExerciseType.CROSS_BODY_PUNCH];
  exerciseState.activeSide = side;
  
  // Also update FSM if implemented
  setCrossBodyPunchFSMActiveSide(side);
}

export function setSeatedKneeExtensionsActiveLeg(leg) {
  const exerciseState = exerciseStates[ExerciseType.SEATED_KNEE_EXTENSIONS];
  exerciseState.activeLeg = leg;
  
  // Also update FSM if implemented
  setSeatedKneeExtensionsFSMActiveLeg(leg);
}

export function setSeatedMarchesActiveLeg(leg) {
  const exerciseState = exerciseStates[ExerciseType.SEATED_MARCHES];
  exerciseState.activeLeg = leg;

  // Also update FSM if implemented
  setSeatedMarchesFSMActiveLeg(leg);
}

// CRITICAL: Cleanup function to release MediaPipe PoseLandmarker WASM memory
export function cleanupExerciseDetection() {
  console.log('[ExerciseDetection] Cleaning up pose landmarker and exercise state');

  // Close pose landmarker to release WASM memory
  if (poseLandmarker) {
    try {
      poseLandmarker.close();
      poseLandmarker = null;
      console.log('[ExerciseDetection] PoseLandmarker closed successfully');
    } catch (error) {
      console.warn('[ExerciseDetection] Error closing PoseLandmarker:', error);
    }
  }

  // Reset exercise state
  Object.keys(exerciseStates).forEach(key => {
    if (exerciseStates[key].fsm) {
      exerciseStates[key].fsm.reset();
    }
  });

  // Reset repetition counts
  Object.keys(repetitionCounts).forEach(key => {
    repetitionCounts[key] = 0;
  });

  // Clear init marker
  try {
    localStorage.removeItem(EXERCISE_INIT_MARKER_KEY);
  } catch(e) {
    // localStorage not available
  }

  console.log('[ExerciseDetection] Cleanup complete');
}